import {
  Button,
  CheckIcon,
  Modal,
  PostItNote,
  QuestionnaireDashboardContainer,
  SendIcon
} from '@st/theme'
import { formatISO8601Date, formatISO8601Time } from '@st/util/time'
import { match, P } from 'ts-pattern'
import { useSDK } from '~/features/app-deps-provider'
import { useAuthClient } from '~/features/app-hooks'
import {
  formatFolderEntityName,
  selDocumentChecklistItems,
  selMissingReasonOptionsListByConstraintId,
  selNonDocumentChecklistItems,
  stFolderModule
} from '~/features/st-folder-viewer/st-folder-module'
import { useProcess, useProcessState } from '~/store'
import { ClientQuestionnaireCard } from './client-questionnaire-card'
import { ClientUploadDocumentsCard } from './client-upload-documents-card'
import { stQuestionnaireModule } from './st-questionnaire-module'
import { SubmitClientOrganizerDialog } from './submit-client-organizer-dialog'
import { ClientOrganizerSection } from '@st/sdk/src/sdk.types'
import { humanJoin } from '@st/util/inflect'

export function STQuestionnaireDashboard() {
  const sdk = useSDK()
  const authClient = useAuthClient()
  const stQuestionnaire = useProcess(stQuestionnaireModule)

  const organization = useProcessState(stQuestionnaireModule, (s) => s.organization)
  const folder = useProcessState(stFolderModule, (s) => s.folderState!.folder)

  const isPreview = useProcessState(stQuestionnaireModule, (s) => s.preview)
  const isLocked = folder.clientOrganizer.status == 'submitted'

  const nonDocumentChecklistItems = useProcessState(stFolderModule, selNonDocumentChecklistItems)

  const dashboardState = useProcessState(stQuestionnaireModule, (s) => s.dashboard)

  const missingReasonOptionsByConstraintIdSubmit = useProcessState(stFolderModule, (s) =>
    selMissingReasonOptionsListByConstraintId(s.folderState!)
  )
  const documentChecklistItemsSubmit = useProcessState(stFolderModule, selDocumentChecklistItems)

  return (
    <>
      <QuestionnaireDashboardContainer
        header={
          <header className="sticky top-0 flex h-12 w-full flex-row items-center justify-between border-b bg-white px-14">
            {organization.name}
            {!isPreview && (
              <Button variant="link" onClick={() => authClient.logout()}>
                Logout
              </Button>
            )}
          </header>
        }
        footer={
          <footer className="flex w-full flex-row items-center justify-between gap-2 border-b bg-blue-500 px-7 py-2">
            <div />

            <div className="flex flex-row items-center gap-3">
              {folder.clientOrganizer.status == 'submitted' &&
              folder.clientOrganizer.submittedAt ? (
                <span className="text-sm text-white">
                  Submitted on {formatISO8601Date(folder.clientOrganizer.submittedAt)} at{' '}
                  {formatISO8601Time(folder.clientOrganizer.submittedAt)}
                </span>
              ) : null}
              <Button
                size="l"
                className="bg-white hover:bg-white/90 active:bg-white/80"
                variant="default"
                leadingIcon={
                  folder.clientOrganizer.status == 'submitted' ? (
                    <CheckIcon className="size-4" />
                  ) : (
                    <SendIcon className="size-4" />
                  )
                }
                disabled={folder.clientOrganizer.status == 'submitted'}
                onClick={() => {
                  stQuestionnaire.send({ type: 'openSubmitDialog' })
                }}
              >
                {folder.clientOrganizer.status == 'submitted' ? 'Submitted' : 'Submit'}
              </Button>
            </div>
          </footer>
        }
      >
        <h1 className="text-center text-3xl font-semibold text-gray-900">
          {formatFolderEntityName(folder.entities, 'client')}, {folder.type} - Tax Year{' '}
          {folder.year}
        </h1>

        <p className="mt-2 text-center text-base text-gray-700">
          Once you have {clientOrganizerActions(folder.clientOrganizer.enabledSections)} below,
          click Submit.
          <br />
          Your progress will always be saved automatically, and you can always come back to upload
          additional documents.
        </p>

        <div className="mt-8 flex flex-col gap-12">
          {match(folder.clientOrganizer.status)
            .with('submitted', () => (
              <PostItNote signature={organization.name} variant="success" className="w-full">
                {`Thank you for submitting your information. You can come back at any time to upload additional documents. We'll be in touch soon.`}
              </PostItNote>
            ))
            .otherwise(() => null)}

          {folder.clientOrganizer.enabledSections.map((section, index) => {
            const sectionsCount = folder.clientOrganizer.enabledSections.length
            const stepNumber = sectionsCount > 1 ? (index + 1).toString() : undefined

            return match(section)
              .with('documents', () => (
                <ClientUploadDocumentsCard key={section} step={stepNumber} isLocked={isLocked} />
              ))
              .with('questionnaire', () => (
                <ClientQuestionnaireCard
                  step={stepNumber}
                  folder={folder}
                  nonDocumentChecklistItems={nonDocumentChecklistItems}
                  onClickEnterQuestionnaire={() =>
                    stQuestionnaire.send({ type: 'enterQuestionnaire' })
                  }
                  isLocked={isLocked}
                />
              ))
              .exhaustive()
          })}
        </div>
      </QuestionnaireDashboardContainer>

      {match(dashboardState)
        .with({ submitDialog: P.nonNullable }, ({ submitDialog }) => {
          return (
            <Modal isOpen={true}>
              <SubmitClientOrganizerDialog
                submitDialog={submitDialog}
                documentChecklistItems={documentChecklistItemsSubmit}
                nonDocumentChecklistItems={nonDocumentChecklistItems}
                missingReasonOptionsByConstraintId={missingReasonOptionsByConstraintIdSubmit}
                onCancel={() => stQuestionnaire.send({ type: 'closeSubmitDialog' })}
                onSubmit={async (incompleteReasonOptions) => {
                  await sdk.send({
                    type: 'folders/setChecklistItemIncompleteReasons',
                    folderId: folder.id,
                    options: incompleteReasonOptions
                  })
                  stQuestionnaire.send({ type: 'submitClientOrganizer', forceSubmit: false })
                }}
              />
            </Modal>
          )
        })
        .otherwise(() => null)}
    </>
  )
}

function clientOrganizerActions(sections: ClientOrganizerSection[]) {
  const actionPhrases: string[] = sections.map((section) => {
    return match(section)
      .with('documents', () => 'uploaded your documents')
      .with('questionnaire', () => 'completed the questionnaire')
      .exhaustive()
  })
  return humanJoin(actionPhrases, 'and')
}
